import { Dispatch } from "react";
import * as actionTypes from "../actionTypes/Index";
import {
  IActionType,
  IGetQueryParams,
  IClientsResponseObject,
  ICreateUpdateAccessControlUserNormalizedReq,
  IFilterPayload,
  IDropDownResponse,
} from "../../interfaces/Index";
import {
  fetchAccessControlUserApiCall,
  getAccessControlUserDetailsNewApiCall,
  getAllUserRoleAndClientsAPICall,
  addUserToClient,
  addUserToRole,
  fetchAllFiltersApiCall,
} from "../sources/AccessControlSource";
import userSource, {
  UserAddUpdateDto,
  UserInfoDto,
} from "../sources/UserSource";

export const fetchAccessControlUserInit = () => ({
  type: actionTypes.GET_ACCESS_CONTROL_USERS_INIT,
});

export const fetchAccessControlUserSuccess = (payload: Array<any> | null) => ({
  type: actionTypes.GET_ACCESS_CONTROL_USERS_SUCCESS,
  payload,
});

export const fetchAccessControlUserFail = (error: string | object | null) => ({
  type: actionTypes.GET_ACCESS_CONTROL_USERS_FAIL,
  error,
});

export const clearAccessControlUserLevelDetails = () => ({
  type: actionTypes.CLEAR_ACCESS_CONTROL_USER_LEVEL_DETAILS,
});

export const getAccessControlUserDetailsNewInit = () => ({
  type: actionTypes.GET_ACCESS_CONTROL_USER_DETAIL_V2_INIT,
});

export const getAccessControlUserDetailsNewSuccess = (payload: Array<any>) => ({
  type: actionTypes.GET_ACCESS_CONTROL_USER_DETAIL_V2_SUCCESS,
  payload,
});

export const getAccessControlUserDetailsNewFail = (
  error: string | object | null
) => ({
  type: actionTypes.GET_ACCESS_CONTROL_USER_DETAIL_V2_FAIL,
  error,
});

export const fetchAllUserRolesAndClientInit = () => ({
  type: actionTypes.GET_ALL_CLIENT_ROLES_INIT,
});

export const fetchAllUserRolesAndClientSuccess = (
  payload: IClientsResponseObject[] | null
) => ({
  type: actionTypes.GET_ALL_CLIENT_ROLES_SUCCESS,
  payload,
});

export const fetchAllUserRolesAndClientFail = (
  error: string | object | null
) => ({
  type: actionTypes.GET_ALL_CLIENT_ROLES_FAIL,
  error,
});

export const createAccessControlUserWithClientsAndRolesInit = () => ({
  type: actionTypes.CREATE_ACCESS_CONTROL_USERS_INIT,
});

export const createAccessControlUserWithClientsAndRolesSuccess = () => ({
  type: actionTypes.CREATE_ACCESS_CONTROL_USERS_SUCCESS,
});

export const createAccessControlUserWithClientsAndRolesFail = (
  error: string | object | null
) => ({
  type: actionTypes.CREATE_ACCESS_CONTROL_USERS_FAIL,
  error,
});

export const updateAccessControlUserWithClientsAndRolesInit = () => ({
  type: actionTypes.UPDATE_ACCESS_CONTROL_USERS_INIT,
});

export const updateAccessControlUserWithClientsAndRolesSuccess = () => ({
  type: actionTypes.UPDATE_ACCESS_CONTROL_USERS_SUCCESS,
});

export const updateAccessControlUserWithClientsAndRolesFail = (
  error: string | object | null
) => ({
  type: actionTypes.UPDATE_ACCESS_CONTROL_USERS_FAIL,
  error,
});

export const fetchAllFiltersInit = () => ({
  type: actionTypes.FETCH_ALL_FILTERS_INIT,
});

export const fetchAllFiltersSuccess = (payload: IFilterPayload[] | null) => ({
  type: actionTypes.FETCH_ALL_FILTERS_SUCCESS,
  payload,
});

export const clearAccessControlUserLevelDetailsAction =
  () => (dispatch: Dispatch<IActionType>) => {
    return dispatch(clearAccessControlUserLevelDetails());
  };

export const fetchAllFiltersFail = (error: string | object | null) => ({
  type: actionTypes.FETCH_ALL_FILTERS_FAIL,
  error,
});

// async actions
export const getAccessControlUsers =
  (queryParams?: IGetQueryParams) => (dispatch: Dispatch<IActionType>) => {
    dispatch(fetchAccessControlUserInit());
    return fetchAccessControlUserApiCall(queryParams)
      .then((accessControlUsers: any) =>
        dispatch(fetchAccessControlUserSuccess(accessControlUsers))
      )
      .catch((e: object | null | string) =>
        dispatch(fetchAccessControlUserFail(e))
      );
  };

export const getAccessControlUserDetailsNewAction =
  (id: string) => (dispatch: Dispatch<IActionType>) => {
    dispatch(getAccessControlUserDetailsNewInit());
    return getAccessControlUserDetailsNewApiCall(id)
      .then((details: any) =>
        dispatch(getAccessControlUserDetailsNewSuccess(details))
      )
      .catch((e: object | null | string) =>
        dispatch(getAccessControlUserDetailsNewFail(e))
      );
  };

export const getAllRolesAndClients =
  () => (dispatch: Dispatch<IActionType>) => {
    dispatch(fetchAllUserRolesAndClientInit());
    return getAllUserRoleAndClientsAPICall()
      .then((data: IClientsResponseObject[]) =>
        dispatch(fetchAllUserRolesAndClientSuccess(data))
      )
      .catch((e: object | null | string) =>
        dispatch(fetchAllUserRolesAndClientFail(e))
      );
  };

export const createAccessControlUserWithClientAndRoles =
  (payload: ICreateUpdateAccessControlUserNormalizedReq) =>
  (dispatch: Dispatch<IActionType>) => {
    dispatch(createAccessControlUserWithClientsAndRolesInit());
    const createUserPayload: any = {
      name: payload.fullName,
      phone: payload.phoneNumber.trim(),
      email: payload.email,
      nationalId: payload.nationalId,
      password: payload.password,
      passwordConfirmation: payload.confirmPassword,
      organizationId: payload.organizationId,
      filters: payload.filters,
    };
    const clientPayload = {
      clientIds: payload.clientIds,
    };
    const rolePayload = {
      roleIds: payload.roleIds,
    };
    return userSource
      .createUser(UserAddUpdateDto.fromCreateDto(null, createUserPayload))
      .then((user: UserInfoDto) => {
        return addUserToClient(user.id, clientPayload).then(() => {
          return addUserToRole(user.id, rolePayload);
        });
      })
      .then(() => dispatch(createAccessControlUserWithClientsAndRolesSuccess()))
      .catch((e: object | null | string) =>
        dispatch(createAccessControlUserWithClientsAndRolesFail(e))
      );
  };

export const updateAccessControlUserWithClientAndRoles =
  (payload: ICreateUpdateAccessControlUserNormalizedReq) =>
  (dispatch: Dispatch<IActionType>) => {
    dispatch(updateAccessControlUserWithClientsAndRolesInit());
    const updateUserPayload: any = {
      name: payload.fullName,
      phone: payload.phoneNumber,
      email: payload.email,
      nationalId: payload.nationalId,
      password: payload.password,
      passwordConfirmation: payload.confirmPassword,
      status: payload.status,
      organizationId: payload.organizationId,
      filters: payload.filters,
    };
    const clientPayload = {
      clientIds: payload.clientIds,
    };
    const rolePayload = {
      roleIds: payload.roleIds,
    };

    return userSource
      .updateUserForAccessControl(
        payload.id as string,
        UserAddUpdateDto.fromUpdateDto(payload.id as string, updateUserPayload)
      )
      .then((user: UserInfoDto) => {
        return addUserToClient(payload.id as string, clientPayload).then(() => {
          return addUserToRole(payload.id as string, rolePayload);
        });
      })
      .then(() => dispatch(updateAccessControlUserWithClientsAndRolesSuccess()))
      .catch((e: object | null | string) =>
        dispatch(updateAccessControlUserWithClientsAndRolesFail(e))
      );
  };

export const fetchAllFilters = () => (dispatch: Dispatch<IActionType>) => {
  dispatch(fetchAllFiltersInit());
  return fetchAllFiltersApiCall()
    .then((data: IFilterPayload[]) => dispatch(fetchAllFiltersSuccess(data)))
    .catch((e: object | null | string) => dispatch(fetchAllFiltersFail(e)));
};
